import { LeftRight } from "@/components/LeftRight.tsx";
import { WSelect, type ConfiguredWSelectProps } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { ResourceIcon } from "@/components/icon/ResourceIcon.tsx";
import { UnassignedItem } from "@/modules/network/UnassignedItem.tsx";
import {
    UNASSIGNED_RESOURCE,
    getDisplayName,
    getResourceStringId,
    type IpAssignableResource,
} from "@/modules/network/ipAddress/resourceId.ts";
import { ipAssignableQueryAtom } from "@/modules/network/ipAddress/resourceQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";

export interface AssignToSelectProps extends ConfiguredWSelectProps<
    IpAssignableResource | typeof UNASSIGNED_RESOURCE,
    string
> {}

function AssignToSelectCustom(props: AssignToSelectProps) {
    return (
        <WSelect
            aria-label="Select resource to assign to"
            getKey={getResourceStringId}
            getTextValue={getDisplayName}
            searchItems
            viewer={(item) =>
                item !== UNASSIGNED_RESOURCE && item !== undefined ? (
                    <LeftRight
                        icon={<ResourceIcon item={item} size="size-1.1em mb-[-0.25em]" />}
                        title={getDisplayName(item)}
                    />
                ) : (
                    <span className="text-muted">
                        <UnassignedItem />
                    </span>
                )
            }
            renderUnknown={() => (
                <span className="text-error">
                    <Trans>Unknown resource</Trans>
                </span>
            )}
            {...props}
        >
            {(item) =>
                item !== UNASSIGNED_RESOURCE ? (
                    <LeftRight icon={<ResourceIcon item={item} />} title={getDisplayName(item)} />
                ) : (
                    <UnassignedItem />
                )
            }
        </WSelect>
    );
}

export interface AssignToSelectLoaderProps extends Omit<AssignToSelectProps, "items"> {
    location: string;
}

function AssignToSelectLoader({ location, ...props }: AssignToSelectLoaderProps) {
    const items = useSuspenseQueryAtom(ipAssignableQueryAtom(location));

    return <AssignToSelectCustom items={[UNASSIGNED_RESOURCE, ...items]} {...props} />;
}

export function AssignToSelect(props: AssignToSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <AssignToSelectCustom {...props} {...renderProps} />}>
            <AssignToSelectLoader {...props} />
        </WSelectWrapper>
    );
}
